import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { rio } from '@amzn/rio-design-tokens';
import { generatePath, useNavigate, useSearchParams, useParams } from 'react-router';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import { StarRating } from 'src/common/starRating/StarRating';
import { DealType } from '@amzn/handshake-painter-typescript-client/v1';
import { RioText, DealText, DiscountText, TruncatedParagraph, Paragraph } from 'src/common/styled';
import { PrimeBadge } from 'src/common/icons';
import { LargePrice } from 'src/common/largePrice/LargePrice';
import { parsePriceComponents, formatPrice } from 'src/utils/priceUtils';
import { formatRating, formatReviewCount, formatCompactReviewCount } from 'src/utils/reviewUtils';
import { YellowButton } from '../buttons';
import { routes } from 'src/utils/routes';
import { selectCarouselAsins, serializeAsins } from 'src/utils/asinListUtils';
import { withAutoCrop } from 'src/utils/imageUtils';
import { addAsinToCreatorAttributionToken, getAsinFromCreatorAttributionToken, } from 'src/utils/creatorAttributionUtils';
import { useCreatorAttributionStore } from 'src/store';
import { usePageContext } from 'src/hooks';
/** Star rating is out of 5. */
const MAX_STAR_RATING = 5;
/** Longest-edge (px) for the featured card's auto-cropped image; ~2x the 146px tile well. */
const FEATURED_IMAGE_EDGE = 300;
const CardImage = styled.img `
  // Retain aspect ratio by height
  width: auto;
  height: 100%;
  // Control max dimensions
  max-width: 100%;
  // Featured: fit the whole photo inside the tile (contain, centered); storefront unchanged.
  ${({ isFeatured }) => isFeatured &&
    `
      height: auto;
      max-height: 100%;
      object-fit: contain;
    `}
`;
const ImageContainer = styled.div `
  // White background for all layouts (CLP grey image tile deferred; TODO(ACESprints-3931)).
  background: ${({ theme }) => theme.rio.semantic.color.background.primary};
  border-radius: ${({ isFeatured }) => (isFeatured ? '12px' : '8px')};
  overflow: hidden;

  // Featured 146px (CLP-measured); storefront 150px; non-storefront 136px.
  height: ${({ useStorefrontLayout, isFeatured }) => (isFeatured ? '146px' : useStorefrontLayout ? '150px' : '136px')};
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;
// Featured card root: plain div (carries the card onClick, which Meridian Column won't forward); first child stretches full-width, CTA slot pins to bottom.
const FeaturedRoot = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
  box-sizing: border-box;
  & > *:first-of-type {
    width: 100%;
  }
`;
// Pins the CTA to the card bottom, full width; button font scoped here so the shared YellowButton is unchanged elsewhere.
const FeaturedButtonSlot = styled.div `
  margin-top: auto;
  padding-top: 8px;
  width: 100%;
  & > * {
    width: 100%;
  }
  & button {
    font-size: ${rio.semantic.size.font.small};
    min-height: 32px;
    height: 32px;
  }
`;
// Featured price/discount sizing, scoped to the featured card; !important beats Meridian Text's ID-specificity rule.
const FeaturedPrice = styled.div `
  [data-testid='product-card-discount'] {
    font-size: ${rio.semantic.size.font.base} !important;
  }
  [data-testid='base-amount'] {
    font-size: ${rio.semantic.size.font.medium} !important;
  }
  [data-testid='base-amount'] b {
    font-weight: ${rio.semantic.fontWeight.regular};
  }
  // 10px sup text (cents/currency) has no matching rio font token; CLP uses this exact size.
  [data-testid='currency-symbol'],
  [data-testid='fractional-amount'] {
    font-size: 10px !important;
  }
`;
// Featured delivery line at mini size, scoped so the shared Paragraph is unchanged elsewhere.
const FeaturedDelivery = styled.div `
  & > * {
    font-size: ${rio.semantic.size.font.mini} !important;
  }
`;
export const ProductCard = ({ productItem, isStorefront = false, isFeatured = false, storefrontAsins, activeCategory, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { aci } = useParams();
    const { getCreatorAttributionToken, setCreatorAttributionToken } = useCreatorAttributionStore();
    const { localizationContext } = useLocalizationContext();
    const locale = localizationContext.getLocale();
    const { commonBundle: bundle } = usePageContext();
    // isFeatured implies the storefront layout; a caller passes only isFeatured.
    const useStorefrontLayout = isStorefront || isFeatured;
    const onClick = () => {
        // Handle navigation
        const currentParams = new URLSearchParams(searchParams);
        if (isStorefront && aci) {
            // Set ACI in current parameters
            currentParams.set('aci', aci);
            // Handle ASIN token logic
            const currentToken = getCreatorAttributionToken();
            const currentAsin = getAsinFromCreatorAttributionToken(currentToken);
            if (currentAsin !== productItem.asin) {
                const newToken = addAsinToCreatorAttributionToken(currentToken, productItem.asin);
                setCreatorAttributionToken(newToken);
            }
        }
        if (storefrontAsins) {
            // Featured cards forward only a relevant window of the collection; storefront forwards the full list.
            if (isFeatured) {
                const carouselAsins = selectCarouselAsins(storefrontAsins.split(','), productItem.asin);
                if (carouselAsins.length > 0) {
                    currentParams.set('asins', serializeAsins(carouselAsins));
                }
            }
            else {
                currentParams.set('asins', storefrontAsins);
            }
        }
        // Stamp storefront history entry with tab + scroll state before navigating
        if (activeCategory) {
            navigate(window.location.pathname + window.location.search, {
                replace: true,
                state: { activeCategory, scrollY: window.scrollY, preventScrollReset: true },
            });
        }
        const dpRoute = generatePath(routes.productDetailPage, { asin: productItem.asin });
        navigate(`${dpRoute}?${currentParams.toString()}`);
    };
    const rawImageUrl = ((_b = (_a = productItem.images.imageList) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.highRes) || ((_d = (_c = productItem.images.imageList) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.lowRes) || '';
    // Featured cards use the CLP-style auto-cropped render; storefront/PDP keep the raw master (withAutoCrop no-ops off-Amazon).
    const imageUrl = isFeatured ? withAutoCrop(rawImageUrl, FEATURED_IMAGE_EDGE) : rawImageUrl;
    const imageAlt = productItem.images.altText || productItem.title;
    // Parse price components for LargePrice component
    const priceComponents = productItem.price && !productItem.price.shouldHidePrice
        ? parsePriceComponents(productItem.price.amount, productItem.price.currencyCode, locale)
        : null;
    const formattedPrice = productItem.price && !productItem.price.shouldHidePrice
        ? formatPrice(productItem.price.amount, productItem.price.currencyCode, locale)
        : '';
    const rating = ((_e = productItem.reviews) === null || _e === void 0 ? void 0 : _e.rating) || 0;
    const reviewCount = ((_f = productItem.reviews) === null || _f === void 0 ? void 0 : _f.count) || 0;
    const hasReviewCount = reviewCount > 0;
    // Featured-only accessible label; gated on hasReviewCount so a no-reviews product is not
    // labelled "0 reviews". reviewCount stays a raw number for the ICU plural argument.
    const ratingAriaLabel = isFeatured && hasReviewCount
        ? bundle.formatMessage('starRatingWithCount', {
            rating: formatRating(rating, locale),
            maxRating: MAX_STAR_RATING,
            reviewCount,
        })
        : undefined;
    const Root = isFeatured ? FeaturedRoot : 'div';
    // Same CTA for both layouts; only its tree position differs (featured pins it to the card bottom, below).
    const seeMoreButton = (_jsx(YellowButton, { height: "small", size: "small", "data-testid": "product-card-see-more-button", children: bundle.getMessage('seeMore') }));
    return (_jsxs(Root, { onClick: onClick, "data-testid": "product-card", children: [_jsxs(Column, { spacing: "200", width: useStorefrontLayout ? '100%' : '136px', children: [_jsx(ImageContainer, { useStorefrontLayout: useStorefrontLayout, isFeatured: isFeatured, children: _jsx(CardImage, { loading: useStorefrontLayout ? 'eager' : 'lazy', src: imageUrl, alt: imageAlt, isFeatured: isFeatured, "data-testid": "product-card-image" }) }), _jsx(TruncatedParagraph, { clamp: 2, type: "b200", "data-testid": "product-card-title", children: productItem.title }), _jsxs(Row, { spacing: "200", alignmentVertical: "center", role: ratingAriaLabel ? 'img' : undefined, "aria-label": ratingAriaLabel, children: [isFeatured && hasReviewCount && (_jsx(RioText, { rioColor: "primary", type: "b100", "data-testid": "product-card-rating-value", children: formatRating(rating, locale) })), _jsx(StarRating, { rating: rating }), hasReviewCount && (_jsxs(RioText, { rioColor: "secondary", type: "b100", children: ["(", isFeatured ? formatCompactReviewCount(reviewCount, locale) : formatReviewCount(reviewCount, locale), ")"] }))] }), !productItem.buyable ? (_jsx(RioText, { rioColor: "error", type: "b200", truncate: true, children: bundle.getMessage('currentlyUnavailable') })) : (_jsxs(_Fragment, { children: [(() => {
                                var _a;
                                const priceRow = (_jsxs(Row, { spacing: "200", alignmentVertical: "center", children: [((_a = productItem.deal) === null || _a === void 0 ? void 0 : _a.discountPercentage) && (_jsxs(DiscountText, { type: "b500", "data-testid": "product-card-discount", children: ["-", productItem.deal.discountPercentage, "%"] })), priceComponents && (_jsx(LargePrice, { size: "small", currencySymbol: priceComponents.currencySymbol, basePart: priceComponents.basePart, fractionalPart: priceComponents.fractionalPart, currencySymbolPosition: priceComponents.currencySymbolPosition, formattedPrice: formattedPrice }))] }));
                                return isFeatured ? _jsx(FeaturedPrice, { children: priceRow }) : priceRow;
                            })(), productItem.deal && productItem.deal.type !== DealType.None && (_jsx(DealText, { type: "b100", children: _jsx("b", { children: bundle.getMessage('deal') }) })), productItem.deal && productItem.originalPrice && !productItem.originalPrice.shouldHidePrice && (_jsx(RioText, { type: "b200", children: bundle.formatMessage('listPrice', {
                                    price: formatPrice(productItem.originalPrice.amount, productItem.originalPrice.currencyCode, locale),
                                    s: (chunk) => _jsx("s", { children: chunk }, chunk),
                                }) })), ((_g = productItem.prime) === null || _g === void 0 ? void 0 : _g.isPrime) && _jsx(PrimeBadge, {}), !!((_h = productItem.deliveryMessage) === null || _h === void 0 ? void 0 : _h.length) &&
                                (() => {
                                    const deliveryLine = (_jsx(Paragraph, { type: "b200", rioColor: isFeatured ? 'primary' : 'secondary', children: productItem.deliveryMessage.map((msg, index) => msg.isBold ? _jsx("b", { children: msg.text }, index) : _jsx("span", { children: msg.text }, index)) }));
                                    return isFeatured ? _jsx(FeaturedDelivery, { children: deliveryLine }) : deliveryLine;
                                })()] })), useStorefrontLayout && productItem.buyable && !isFeatured && seeMoreButton] }), isFeatured && productItem.buyable && _jsx(FeaturedButtonSlot, { children: seeMoreButton })] }));
};
